import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Stepper, Step, StepLabel, Button, TextField,
  Alert, CircularProgress, Paper, IconButton, MenuItem, Snackbar,
  FormControl, RadioGroup, FormControlLabel, Radio, InputLabel, Select, InputAdornment,
  Collapse, Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Schedule as ScheduleIcon,
  Storage as StorageIcon,
  PlayArrow as PlayArrowIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import connectorSchemas from '../../connector_schemas.json';

import PageBreadcrumbs from '../common/PageBreadcrumbs';
import FieldMappingBuilder from './FieldMappingBuilder';
import ScheduleBuilder from './ScheduleBuilder';

import { 
  createJob 
} from '../../redux/actions/ingestion/jobs';
import { 
  getSchema,
  listSources
} from '../../redux/actions/ingestion/sources';
import {
  fetchRequest as testRestCallRequest,
  clear as clearTestRestCallAction,
} from '../../redux/actions/ingestion/testRestCall';
import { suggestName, clearSuggestedName } from '../../redux/actions/utilities';

const steps = [
  'Basic Information',
  'Query Configuration',
  'Field Mapping',
  'Schedule Setup'
];

// Helper function to truncate URL in the middle
const truncateMiddle = (str, maxLength = 50) => {
  if (!str || str.length <= maxLength) return str;
  const charsToShow = maxLength - 3; // Account for "..."
  const frontChars = Math.ceil(charsToShow / 2);
  const backChars = Math.floor(charsToShow / 2);
  return str.substring(0, frontChars) + '...' + str.substring(str.length - backChars);
};

const CreateJob = ({ 
  sources,
  schemas,
  isCreating,
  createError,
  createdJob,
  suggestedName,
  isLoadingName,
  isTestingApi,
  testRestResponse,
  testRestError,
  createJob,
  getSchema,
  listSources,
  suggestName,
  clearSuggestedName,
  testRestCall,
  clearTestRestCall
}) => {
  const navigate = useNavigate();
  const { sourceId } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const [jobData, setJobData] = useState({
    source_id: sourceId,
    name: '',
    description: '',
    config: {
      query: '',
      parameters: {},
      field_mappings: [],
      timestamp: 'auto', // 'finegrained' | 'auto' | 'fixed'
      options: {
        delete_existing: false,
        skip_duplicates: false,
        chunk_size: 25000
      }
    },
    schedule: {
      type: 'once',
      enabled: true
    }
  });
  
  // REST API testing states
  const [apiResponse, setApiResponse] = useState(null);
  const [apiError, setApiError] = useState(null);
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

  const selectedSource = sources.find(s => s.source_id === sourceId);
  const sourceSchema = schemas[sourceId];
  
  // Test the REST API through the proxy endpoint (the request itself lives
  // in the ingestion/testRestCall saga).
  const testRestApiProxy = () => {
    setApiError(null);
    setApiResponse(null);
    testRestCall({
      sourceId,
      endpoint: jobData.config.endpoint,
      queryParams: jobData.config.query_params,
      method: jobData.config.method,
      requestBody: jobData.config.request_body,
    });
  };

  // Reset the test slice when leaving the wizard.
  useEffect(() => () => { clearTestRestCall(); }, [clearTestRestCall]);

  useEffect(() => {
    if (!testRestResponse) return;
    clearTestRestCall();
    setApiResponse(testRestResponse.data);
    // Auto-populate endpoint in job config if not set (written straight into
    // jobData so it doesn't trip the "clear API response on endpoint change"
    // logic in handleInputChange).
    if (!jobData.config.endpoint && testRestResponse.metadata?.endpoint) {
      const endpoint = testRestResponse.metadata.endpoint;
      setJobData(prev => ({
        ...prev,
        config: {
          ...prev.config,
          endpoint
        }
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testRestResponse, clearTestRestCall]);

  useEffect(() => {
    if (!testRestError) return;
    clearTestRestCall();
    setApiError(testRestError);
  }, [testRestError, clearTestRestCall]);

  useEffect(() => {
    // Load sources if not already loaded
    if (!sources || sources.length === 0) {
      listSources();
    }
    
    // Load schema if needed
    if (sourceId && !sourceSchema) {
      getSchema(sourceId);
    }
  }, [sourceId, sourceSchema, sources, getSchema, listSources]);

  useEffect(() => {
    if (createdJob) {
      navigate(`/datasources/${sourceId}`);
    }
  }, [createdJob, navigate, sourceId]);

  // Request name suggestion when component loads
  useEffect(() => {
    suggestName('', 'Job');
  }, [suggestName]);

  // Update form with suggested name
  useEffect(() => {
    if (suggestedName) {
      setJobData(prev => ({
        ...prev,
        name: suggestedName
      }));
    }
  }, [suggestedName]);

  // Auto-switch to finegrained timestamp mode when datetime fields are detected
  useEffect(() => {
    // Only check if we have field mappings
    if (jobData.config.field_mappings && jobData.config.field_mappings.length > 0) {
      const hasDateTimeField = jobData.config.field_mappings.some(mapping => 
        mapping.data_type === 'datetime_timestamp' || 
        mapping.data_type === 'datetime'
      );

      // Only auto-switch if currently on 'auto' mode and datetime fields exist
      if (hasDateTimeField && jobData.config.timestamp === 'auto') {
        setJobData(prev => ({
          ...prev,
          config: {
            ...prev.config,
            timestamp: 'finegrained'
          }
        }));
      }
    }
  }, [jobData.config.field_mappings.length, jobData.config.timestamp]);

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate(`/datasources/${sourceId}`);
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Clear API response when endpoint changes (for REST APIs)
    if (name === 'config.endpoint' && selectedSource?.connector_type === 'rest') {
      setApiResponse(null);
      setApiError(null);
    }
    
    // Handle timestamp mode changes
    if (name === 'config.timestamp') {
      if (value === 'fixed') {
        // When switching to fixed, clear the fixedTimestamp
        setJobData(prev => ({
          ...prev,
          config: {
            ...prev.config,
            timestamp: value,
            fixedTimestamp: ''
          }
        }));
        return;
      } else {
        // For other modes, just update timestamp
        setJobData(prev => ({
          ...prev,
          config: {
            ...prev.config,
            timestamp: value
          }
        }));
        return;
      }
    }
    
    // Handle fixed timestamp value changes
    if (name === 'config.fixedTimestamp') {
      setJobData(prev => ({
        ...prev,
        config: {
          ...prev.config,
          timestamp: value || 'fixed', // Use the actual value as timestamp
          fixedTimestamp: value
        }
      }));
      return;
    }
    
    if (name.startsWith('config.')) {
      const configField = name.replace('config.', '');
      setJobData(prev => ({
        ...prev,
        config: {
          ...prev.config,
          [configField]: value
        }
      }));
    } else if (name.includes('.')) {
      const parts = name.split('.');
      if (parts.length === 2) {
        const [parent, child] = parts;
        setJobData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: value
          }
        }));
      }
    } else {
      setJobData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleFieldMappingsChange = (mappings) => {
    // Check if there are any datetime fields in the mappings (including enabled and disabled)
    const hasDateTimeField = mappings.some(mapping => 
      mapping.data_type === 'datetime_timestamp' || 
      mapping.data_type === 'datetime'
    );

    setJobData(prev => {
      // Auto-set timestamp mode to 'finegrained' if datetime fields exist and mode is still 'auto'
      const shouldSetFinegrained = hasDateTimeField && prev.config.timestamp === 'auto';

      return {
        ...prev,
        config: {
          ...prev.config,
          field_mappings: mappings,
          timestamp: shouldSetFinegrained ? 'finegrained' : prev.config.timestamp
        }
      };
    });
  };

  const handleScheduleChange = useCallback((schedule) => {
    setJobData(prev => {
      // Only update if the schedule actually changed
      if (JSON.stringify(prev.schedule) === JSON.stringify(schedule)) {
        return prev;
      }
      const newData = {
        ...prev,
        schedule: { ...schedule } // Create a new object to ensure immutability
      };
      return newData;
    });
  }, []);

  const handleCreateJob = () => {
    // Prepare job data with proper timestamp configuration
    const finalJobData = { ...jobData };
    
    // If using fixed timestamp, set the actual ISO string as timestamp
    if (jobData.config.timestamp === 'fixed' && jobData.config.fixedTimestamp) {
      finalJobData.config.timestamp = jobData.config.fixedTimestamp;
      // Remove the temporary fixedTimestamp field
      delete finalJobData.config.fixedTimestamp;
    }
    
    // Filter only enabled mappings and clear is_timestamp flags if not using finegrained mode
    const fieldMappings = jobData.config.field_mappings
      .filter(mapping => mapping.enabled !== false) // Only include enabled fields
      .map(mapping => ({
        ...mapping,
        is_timestamp: jobData.config.timestamp === 'finegrained' ? mapping.is_timestamp : false
      }));
    
    // Get schema configuration for the connector type
    const connectorType = selectedSource?.connector_type;
    const schemaConfig = connectorSchemas[connectorType];
    
    if (schemaConfig) {
      if (schemaConfig.fieldsArray) {
        // Handle connectors that use fields array format
        const fields = fieldMappings.map(mapping => {
          const field = {};
          
          // Map source field
          field[schemaConfig.fieldMappings.source] = mapping.source_field;
          
          // Map target field
          field[schemaConfig.fieldMappings.target] = mapping.target_key;
          
          // Map data type
          field[schemaConfig.fieldMappings.dataType] = mapping.data_type;

          // Friendly display name shown platform-wide (key stays unchanged)
          if (mapping.display_name) {
            field.display_name = mapping.display_name;
          }
          
          // Map timestamp field if configured
          if (schemaConfig.fieldMappings.timestamp) {
            field[schemaConfig.fieldMappings.timestamp] = mapping.is_timestamp;
          }
          
          // Map native type if configured
          if (schemaConfig.fieldMappings.nativeType) {
            const nativeTypeValue = connectorType === 'bigquery' ? (mapping.bigquery_type || 'STRING') :
                                  connectorType === 'postgres' ? (mapping.postgres_type || mapping.sql_type || 'text') :
                                  connectorType === 'mysql' ? (mapping.mysql_type || mapping.sql_type || 'varchar') :
                                  connectorType === 'sqlserver' ? (mapping.sqlserver_type || mapping.sql_type || 'nvarchar') :
                                  'text';
            field[schemaConfig.fieldMappings.nativeType] = nativeTypeValue;
          }
          
          // Handle additional fields for datetime
          if (connectorType === 'rest' && mapping.data_type === 'datetime_timestamp' && mapping.dateFormat) {
            field.date_format = mapping.dateFormat;
            field.date_pattern = mapping.datePattern;
          }
          
          // Add context information for REST APIs
          if (connectorType === 'rest' && mapping.context) {
            field.context = mapping.context;
            if (mapping.arrayPath) {
              field.array_path = mapping.arrayPath;
            }
          }
          
          return field;
        });
        
        finalJobData.config = {
          ...finalJobData.config,
          [schemaConfig.schemaKey]: {
            fields
          }
        };
      } else if (schemaConfig.objectFormat) {
        // Handle file/s3 connectors that use object format
        const schema = {};
        fieldMappings.forEach(mapping => {
          const fieldConfig = {};
          
          // Apply type transformation
          if (schemaConfig.fieldStructure.type.transform) {
            fieldConfig.type = schemaConfig.fieldStructure.type.transform[mapping.data_type] || mapping.data_type;
          } else {
            fieldConfig.type = mapping.data_type;
          }
          
          // Set timestamp field
          fieldConfig[schemaConfig.fieldStructure.timestamp.field] = mapping.is_timestamp;
          
          // Set index field
          fieldConfig[schemaConfig.fieldStructure.index.field] = mapping.originalIndex || schemaConfig.fieldStructure.index.default;
          
          // Handle conditional fields
          if (fieldConfig.type === 'datetime' && mapping.dateFormat) {
            fieldConfig.dateFormat = mapping.dateFormat;
            fieldConfig.datePattern = mapping.datePattern;
          }
          
          // Handle renamed fields
          if (mapping.originalName && mapping.renamed) {
            fieldConfig.originalName = mapping.originalName;
            fieldConfig.renamed = true;
          }

          // Friendly display name shown platform-wide (key stays unchanged)
          if (mapping.display_name) {
            fieldConfig.display_name = mapping.display_name;
          }
          
          schema[mapping.source_field] = fieldConfig;
        });
        
        finalJobData.config = {
          ...finalJobData.config,
          [schemaConfig.schemaKey]: schema
        };
      }
      
      // Remove field_mappings as we've converted to the specific schema format
      delete finalJobData.config.field_mappings;
    } else {
      // Unknown connector type, keep field_mappings
      finalJobData.config.field_mappings = fieldMappings;
    }
    
    createJob(finalJobData);
  };

  const canProceedFromStep = (step) => {
    switch (step) {
      case 0:
        return jobData.name.trim().length > 0;
      case 1:
        // File-based sources don't require queries
        if (selectedSource?.connector_type === 'file') {
          return true;
        }
        // S3 sources require file path
        if (selectedSource?.connector_type === 's3') {
          return jobData.config.file_path?.trim().length > 0;
        }
        // REST APIs require successful API test (endpoint is optional)
        if (selectedSource?.connector_type === 'rest') {
          return apiResponse !== null;
        }
        // GraphQL requires query
        if (selectedSource?.connector_type === 'graphql') {
          return jobData.config.query?.trim().length > 0;
        }
        // Kafka requires topic
        if (selectedSource?.connector_type === 'kafka') {
          return jobData.config.topic?.trim().length > 0;
        }
        // Webhook doesn't require configuration in this step
        if (selectedSource?.connector_type === 'webhook') {
          return true;
        }
        // Database sources require a query
        return jobData.config.query?.trim().length > 0;
      case 2:
        // If using auto or fixed timestamp, we don't need a timestamp field
        if (jobData.config.timestamp === 'auto') {
          // Just need at least one field selected
          if (selectedSource?.connector_type === 'rest') {
            return jobData.config.field_mappings.length > 0;
          }
          const activeMappings = jobData.config.field_mappings.filter(m => m.enabled !== false);
          return activeMappings.length > 0;
        }
        
        // If using fixed timestamp, need valid timestamp and fields
        if (jobData.config.timestamp === 'fixed') {
          const hasValidTimestamp = jobData.config.fixedTimestamp && 
                                    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(jobData.config.fixedTimestamp);
          if (!hasValidTimestamp) return false;
          
          if (selectedSource?.connector_type === 'rest') {
            return jobData.config.field_mappings.length > 0;
          }
          const activeMappings = jobData.config.field_mappings.filter(m => m.enabled !== false);
          return activeMappings.length > 0;
        }
        
        // For finegrained, need at least one timestamp field
        if (selectedSource?.connector_type === 'rest') {
          return jobData.config.field_mappings.length > 0 && 
                 jobData.config.field_mappings.some(m => m.is_timestamp);
        }
        const activeMappings = jobData.config.field_mappings.filter(m => m.enabled !== false);
        return activeMappings.length > 0 && 
               activeMappings.some(m => m.is_timestamp);
      default:
        return true;
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Basic Information
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Provide basic details about this import job
            </Typography>


            <TextField
              fullWidth
              label="Job Name"
              value={jobData.name}
              onChange={handleInputChange}
              name="name"
              placeholder={isLoadingName ? 'Generating name...' : 'Daily Revenue Import'}
              required
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Description (Optional)"
              name="description"
              value={jobData.description}
              onChange={handleInputChange}
              placeholder="Import daily revenue data from BigQuery at 2 AM"
              sx={{ mb: 0 }}
            />

          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Query Configuration
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Configure the query to extract data from your source
            </Typography>

            {selectedSource?.connector_type === 'bigquery' && (
              <TextField
                fullWidth
                multiline
                rows={8}
                label="SQL Query"
                name="config.query"
                value={jobData.config.query}
                onChange={handleInputChange}
                placeholder={`SELECT 
  date,
  revenue,
  costs
FROM \`your-workspace.dataset.table\`
WHERE date > @last_date
ORDER BY date`}
                helperText="Use @parameters for dynamic values (e.g., @last_date for incremental imports)"
                required
                sx={{ fontFamily: 'monospace' }}
              />
            )}

            {selectedSource?.connector_type === 'postgres' && (
              <TextField
                fullWidth
                multiline
                rows={8}
                label="SQL Query"
                name="config.query"
                value={jobData.config.query}
                onChange={handleInputChange}
                placeholder={`SELECT 
  created_at,
  amount,
  category
FROM transactions
WHERE created_at > $1
ORDER BY created_at`}
                helperText="Use $1, $2, etc. for parameters"
                required
                sx={{ fontFamily: 'monospace' }}
              />
            )}

            {selectedSource?.connector_type === 'file' && (
              <Alert severity="info">
                File-based sources don't require queries. The system will automatically process uploaded files.
              </Alert>
            )}

            {selectedSource?.connector_type === 's3' && (
              <Box>
                <TextField
                  fullWidth
                  label="S3 File Path Pattern"
                  name="config.file_path"
                  value={jobData.config.file_path || ''}
                  onChange={handleInputChange}
                  placeholder="exports/daily/*.csv"
                  helperText="Use wildcards (*) to match multiple files"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="File Prefix (Optional)"
                  name="config.prefix"
                  value={jobData.config.prefix || ''}
                  onChange={handleInputChange}
                  placeholder="2024/"
                />
              </Box>
            )}

            {selectedSource?.connector_type === 'rest' && (
              <Box>
                <TextField
                  fullWidth
                  label="API Endpoint"
                  name="config.endpoint"
                  value={jobData.config.endpoint || ''}
                  onChange={handleInputChange}
                  placeholder="/your/api/path (optional)"
                  helperText="Optional path to append to the base URL"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start" sx={{ maxHeight: 'none', mr: 0 }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          borderRight: '2px solid',
                          borderColor: 'divider',
                          pr: 0.75,
                          mr: 0,
                          ml: -1.75,
                          pl: 1.75,
                          py: 1.9,
                          my: -0.95,
                          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.06)',
                          cursor: 'not-allowed'
                        }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: 'monospace',
                              color: 'text.disabled',
                              fontSize: '0.875rem',
                              userSelect: 'none'
                            }}
                          >
                            {truncateMiddle(selectedSource?.config?.connection?.url || '', 35)}
                          </Typography>
                        </Box>
                      </InputAdornment>
                    )
                  }}
                  sx={{ 
                    mb: 2,
                    '& input': {
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      pl: 0.75
                    }
                  }}
                />
                
                {/* Advanced Settings Toggle */}
                <Box sx={{ mb: 2 }}>
                  <Button
                    onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
                    endIcon={showAdvancedSettings ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ 
                      color: 'text.secondary',
                      '&:hover': {
                        backgroundColor: 'action.hover'
                      }
                    }}
                  >
                    Advanced Settings
                  </Button>
                  <Divider sx={{ mt: 1 }} />
                </Box>

                {/* Advanced Settings Collapsible Section */}
                <Collapse in={showAdvancedSettings} timeout="auto" unmountOnExit>
                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      label="HTTP Method"
                      select
                      name="config.method"
                      value={jobData.config.method || 'GET'}
                      onChange={handleInputChange}
                      sx={{ mb: 2 }}
                    >
                      <MenuItem value="GET">GET</MenuItem>
                      <MenuItem value="POST">POST</MenuItem>
                      <MenuItem value="PUT">PUT</MenuItem>
                      <MenuItem value="PATCH">PATCH</MenuItem>
                    </TextField>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Query Parameters (JSON)"
                      name="config.query_params"
                      value={jobData.config.query_params || '{}'}
                      onChange={handleInputChange}
                      placeholder='{"pair": "BTCUSD", "limit": 100}'
                      helperText="JSON object with query parameters"
                      sx={{ fontFamily: 'monospace', mb: 2 }}
                    />
                    {['POST', 'PUT', 'PATCH'].includes(jobData.config.method) && (
                      <TextField
                        fullWidth
                        multiline
                        rows={6}
                        label="Request Body (JSON)"
                        name="config.request_body"
                        value={jobData.config.request_body || ''}
                        onChange={handleInputChange}
                        placeholder='{"filter": {"status": "active"}, "limit": 100}'
                        helperText="JSON request body for POST/PUT/PATCH requests"
                        sx={{ fontFamily: 'monospace', mb: 2 }}
                      />
                    )}
                  </Box>
                </Collapse>
                
                {/* Test API Button */}
                <Box sx={{ mb: 3 }}>
                  <Button
                    variant="contained"
                    onClick={testRestApiProxy}
                    disabled={isTestingApi}
                    startIcon={isTestingApi ? <CircularProgress size={20} /> : <PlayArrowIcon />}
                  >
                    {isTestingApi ? 'Testing API...' : 'Test API & Preview Response'}
                  </Button>
                </Box>
                
                {/* API Error */}
                {apiError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    <Typography variant="subtitle2">API Test Failed:</Typography>
                    <Typography variant="body2">{apiError}</Typography>
                  </Alert>
                )}
                
                {/* Helper message when not tested */}
                {!apiResponse && !apiError && (
                  <Alert severity="info" sx={{ mb: 3 }}>
                    Please test the API to preview the response and continue to field mapping.
                  </Alert>
                )}
                
                {/* API Response Preview */}
                {apiResponse && (
                  <Box sx={{ mb: 3 }}>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      API connection successful! Response data will be used for field mapping in the next step.
                    </Alert>
                    
                    <Typography variant="subtitle1" gutterBottom>
                      Response Preview:
                    </Typography>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        p: 0, 
                        bgcolor: 'grey.50', 
                        maxHeight: 200, 
                        overflow: 'auto',
                        '& pre': {
                          margin: 0,
                          fontFamily: 'monospace',
                          fontSize: '0.55rem'
                        }
                      }}
                    >
                      <pre>{JSON.stringify(apiResponse, null, 2)}</pre>
                    </Paper>
                  </Box>
                )}
              </Box>
            )}

            {selectedSource?.connector_type === 'graphql' && (
              <Box>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  label="GraphQL Query"
                  name="config.query"
                  value={jobData.config.query || ''}
                  onChange={handleInputChange}
                  placeholder={`query GetData($date: String!) {
  data(date: $date) {
    id
    timestamp
    value
    category
  }
}`}
                  helperText="GraphQL query with variables"
                  required
                  sx={{ fontFamily: 'monospace', mb: 2 }}
                />
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Query Variables (JSON)"
                  name="config.variables"
                  value={jobData.config.variables || '{}'}
                  onChange={handleInputChange}
                  placeholder='{"date": "@last_sync"}'
                  helperText="JSON object with query variables"
                  sx={{ fontFamily: 'monospace' }}
                />
              </Box>
            )}

            {selectedSource?.connector_type === 'kafka' && (
              <Box>
                <TextField
                  fullWidth
                  label="Topic Name"
                  name="config.topic"
                  value={jobData.config.topic || ''}
                  onChange={handleInputChange}
                  placeholder="events.user.activity"
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Consumer Group"
                  name="config.consumer_group"
                  value={jobData.config.consumer_group || ''}
                  onChange={handleInputChange}
                  placeholder="celmind-ingestion"
                  helperText="Kafka consumer group ID"
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Starting Offset"
                  select
                  name="config.offset"
                  value={jobData.config.offset || 'latest'}
                  onChange={handleInputChange}
                >
                  <MenuItem value="latest">Latest</MenuItem>
                  <MenuItem value="earliest">Earliest</MenuItem>
                  <MenuItem value="stored">Stored Offset</MenuItem>
                </TextField>
              </Box>
            )}

            {selectedSource?.connector_type === 'webhook' && (
              <Alert severity="info">
                Webhook sources receive data pushed from external systems. Configure field mappings to process incoming webhook payloads.
              </Alert>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Timestamp Configuration
            </Typography>
            
            <Paper sx={{ p: 2, mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel id="timestamp-mode-label">Timestamp Mode</InputLabel>
                <Select
                  labelId="timestamp-mode-label"
                  value={jobData.config.timestamp}
                  onChange={(e) => handleInputChange({ target: { name: 'config.timestamp', value: e.target.value } })}
                  label="Timestamp Mode"
                >
                  <MenuItem value="finegrained">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      <strong>Field-based:</strong>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                      Select a timestamp field from your data. Best for data that already contains timestamps (logs, events, transactions).
                      </Typography>
                    </Box>
                  </MenuItem>
                  
                  <MenuItem value="auto">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      <strong>Auto-generate:</strong>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                      Timestamps are created when data is ingested. Ideal for real-time data streams without existing timestamps.
                      </Typography>
                    </Box>
                  </MenuItem>
                  
                  <MenuItem value="fixed">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      <strong>Fixed:</strong>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                      All records use the same timestamp. Perfect for historical bulk imports or batch data from a specific time.
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
                
      
                
                {jobData.config.timestamp === 'fixed' && (
                  <TextField
                    fullWidth
                    label="Fixed Timestamp"
                    name="config.fixedTimestamp"
                    value={jobData.config.fixedTimestamp || ''}
                    onChange={handleInputChange}
                    placeholder="2024-10-30T14:00:00Z"
                    helperText="ISO 8601 format (YYYY-MM-DDTHH:mm:ssZ)"
                    sx={{ mt: 2 }}
                  />
                )}
              </FormControl>
            </Paper>
            
            
       
            <FieldMappingBuilder
              schema={sourceSchema}
              mappings={jobData.config.field_mappings}
              onChange={handleFieldMappingsChange}
              connectorType={selectedSource?.connector_type}
              apiResponse={selectedSource?.connector_type === 'rest' ? apiResponse : null}
              timestampMode={jobData.config.timestamp}
              onError={(message) => {
                setSnackbar({
                  open: true,
                  message,
                  severity: 'error'
                });
              }}
            />
          </Box>
        );

      case 3:
        return (
          <Box>
            <ScheduleBuilder
              schedule={jobData.schedule}
              onChange={handleScheduleChange}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  if (!selectedSource) {
    return (
      <Alert severity="error">
        Data source not found. Please select a valid data source.
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      {/* Header */}
      <PageBreadcrumbs 
        items={[
          { 
            text: "Data Sources", 
            link: "/datasources",
            icon: <StorageIcon />
          },
          { 
            text: selectedSource.name, 
            link: `/datasources/${sourceId}`
          },
          { text: "Create Job" }
        ]}
      />

      {/* Content */}
      <Box sx={{ px: 1 }}>


        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Error Alert */}
        {createError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to create import job: {createError.message || createError}
          </Alert>
        )}

        {/* Step Content */}
        <Paper sx={{ p: 3, minHeight: 295 }}>
          {renderStepContent(activeStep)}
        </Paper>

        {/* Navigation Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button onClick={handleBack}>
            {activeStep === 0 ? 'Cancel' : 'Back'}
          </Button>
          
          <Box>
            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!canProceedFromStep(activeStep)}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleCreateJob}
                disabled={isCreating || !canProceedFromStep(activeStep)}
                startIcon={isCreating ? <CircularProgress size={20} /> : <ScheduleIcon />}
              >
                {isCreating ? 'Creating...' : 'Create Import Job'}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  sources: Array.isArray(state.ingestion_sources?.sources) ? state.ingestion_sources.sources : [],
  schemas: state.ingestion_sources?.schemas || {},
  isCreating: state.ingestion_jobs?.isCreating || false,
  createError: state.ingestion_jobs?.createError,
  createdJob: state.ingestion_jobs?.createdJob,
  suggestedName: state.utilities?.suggestedName,
  isLoadingName: state.utilities?.isLoadingName || false,
  isTestingApi: state.ingestion_test_rest_call?.isLoading || false,
  testRestResponse: state.ingestion_test_rest_call?.response,
  testRestError: state.ingestion_test_rest_call?.error
});

const mapDispatchToProps = {
  createJob,
  getSchema,
  listSources,
  suggestName,
  clearSuggestedName,
  testRestCall: testRestCallRequest,
  clearTestRestCall: clearTestRestCallAction
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateJob);
